import { Field, useFormikContext } from 'formik';
import React, { FC, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocale } from '../../contexts/localeContext';
import { TranslationKeys } from '../../contexts/TranslationKeys';
import useFocusOnRouteChange from '../../hooks/useFocusOnRouteChange';
import { Door } from '../../models/Door';
import { Filter } from '../../models/Filter';
import { ListQuery } from '../../models/ListQuery';
import { getReaderNames } from '../../services/readerService';
import { getZoneNamesWithPagination } from '../../services/zoneService';
import { IAppState } from '../../store/types';
import { appendFilter } from '../../utilities/filters';
import { validateItemName } from '../../utilities/Validation';
import EntityHierarchy from '../entityHierarchy/entityHierarchy';
import Label from '../label/label';
import PaginatedSearchableSelectField from '../selection-field/paginated-searchable-select-field';
import { getAvailableTerminals } from '../../services/terminalService';

interface DoorProps {
    decoupleReader?: (readerId) => any,
    openDoor: (entryReader, exitReader) => void,
    setFormikContext: (formikContext) => void,
    currentItem: Door
};

const DoorCard: FC<DoorProps> = ({ decoupleReader = (readerId) => { }, openDoor, setFormikContext, currentItem }) => {
    const { translate } = useLocale();
    const formikContext = useFormikContext<any>();
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const inputRef = useRef<HTMLInputElement>(null);
    useFocusOnRouteChange(inputRef);

    useEffect(() => {
        setFormikContext(formikContext);
    }, []);

    const deleteReader = (readerId, reader)=>{
        if(readerId){
            decoupleReader(readerId)
            formikContext.setFieldValue(reader, "");
        }
    }

    const initialFilter = (filters?: Filter[]) => {
        if (formikContext.values.id) {
            return appendFilter(formikContext.values.controllerId, 'Controller', 'controller.id', filters ?? [])
        }
        
        return appendFilter(formikContext.values.id ? formikContext.values.siteId : siteId, 'Site', 'controller.site.id', filters ?? [])
    }

    const zoneDependencies = () => {
        if (formikContext.values.id) {
            return { accessor: "controllerId", key: "controller.id", name: "Controller", value: formikContext.values.controllerId, childAcceccor: "zoneId" }
        }
        
        return { accessor: "siteId", key: "controller.site.id", name: "Site", value: formikContext.values.id ? formikContext.values.siteId : siteId, childAcceccor: "zoneId" }
    }

    const getReaderTypeFilters = (readerType: "exit" | "entry", filters: Filter[] | undefined) => {
        if(readerType==="entry" && formikContext.values.exitReaderId) {            
            filters = appendFilter(formikContext.values.exitReaderId, "exit reader", "id", filters ?? [], "=out=")
        }
        else if(readerType === "exit" && formikContext.values.entryReaderId) {            
            filters = appendFilter(formikContext.values.entryReaderId, "entry reader", "id", filters ?? [], "=out=")
        }                

        return appendFilter(formikContext.values.zoneId ? formikContext.values.zoneId : 0, "Zone", "controller.zones.id", filters ?? [])
    }

    return <>
        <div className="w-full p-2 inline-flex items-start justify-start  bg-white">
            {formikContext.values.id
                ? <div className="ml-8 mr-5 py-4 card-details">
                    <EntityHierarchy title={formikContext.values.name} tree={{ label: formikContext.values.controller, node: { label: formikContext.values.zone, node: { label: formikContext.values.name } } }} />
                </div>
                : null}
            <div className="flex flex-col py-4 px-8">
                <div className="mb-2">
                    <div className="flex flex-row justify-between" id="nameLabel" >
                        <Label
                            htmlFor="name"
                            className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                        {formikContext.errors.name ? <span className='text-red-600 text-xs'>{formikContext.errors.name}</span> : null}
                    </div>
                    <div className="flex flex-row justify-between">
                        <Field
                            type="text"
                            innerRef={inputRef}
                            name="name"
                            className="flex flex-row items-center pr-2 pl-2 p-1 text-gray-800 text-sm w-full bg-gray-input"
                            validate={(value) => validateItemName(value, translate)} id='nameInputId' />
                    </div>
                </div>
                <div className="mt-2" id='zoneSelectionDiv'>
                    <div className="flex flex-row justify-between" id='zoneLabelId'>
                        <Label
                            htmlFor="zoneId"
                            className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.zone)}:</Label>
                        {formikContext.errors.zoneId ? <span className='text-red-600 text-xs'>{formikContext.errors.zoneId}</span> : null}
                    </div>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.zone)}
                        key="zoneId"
                        accessor='zoneId'
                        dataMethod={(listParameters?: ListQuery) => getZoneNamesWithPagination({ ...listParameters, filters: initialFilter(listParameters?.filters) })}
                        searchAccessor={'zoneName'}
                        required={true}
                        onSelect={(_,item) => {
                            formikContext.setFieldValue('zone', item?.label)
                            formikContext.setFieldValue('zoneId', item?.value)
                        }}
                        dependent={zoneDependencies()}
                        selectedItemLabel={formikContext.values.zone} />
                </div>
                <div className="mb-2">
                    <div className="flex flex-row justify-between">
                        <button
                            type="button"
                            hidden={formikContext.values.entryReaderId || formikContext.values.exitReaderId ? false : true}
                            className="py-1 px-2  mt-3 secondary secondary-enabled" onClick={(e) => openDoor(formikContext.values.entryReaderId, formikContext.values.exitReaderId)}>
                            <span className="mr-2">{translate.byKey(TranslationKeys.openDoor)}</span>
                        </button>
                    </div>
                </div>
            </div>
            <div className="flex flex-col py-4 px-8">
                <div className="mb-2">
                    <div className="flex flex-row justify-between" id='entryReaderId'>
                        <Label
                            htmlFor="entryReaderId"
                            className="text-xs text-gray-600 mt-1" >{translate.byKey(TranslationKeys.entryReader)}:</Label>
                        {formikContext.errors.entryReaderId ? <span className='text-red-600 text-xs'>{formikContext.errors.entryReaderId}</span> : null}
                    </div>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.entryReader)}
                        key="entryReaderId"
                        accessor='entryReaderId'
                        dataMethod={(listParameter: ListQuery) => getAvailableTerminals({ ...listParameter, filters: getReaderTypeFilters("entry",listParameter.filters)})}
                        searchAccessor={'devName'}
                        onDelete={(event) => deleteReader(formikContext.values.entryReaderId, 'entryReaderId')}
                        onSelect={(_,item) => {
                            formikContext.setFieldValue('entryReader', item?.label)
                            formikContext.setFieldValue('entryReaderId', item?.value)                            
                        }}                        
                        selectedItemLabel={formikContext.values.entryReader ?? ""}
                        dependent={{ accessor: 'zoneId', name: 'Zone', key: 'controller.zones.id', value: formikContext.values.zoneId ? formikContext.values.zoneId : 0, childAcceccor: 'entryReaderId' }}
                        disabled={!!currentItem.entryReaderId}/>
                </div>

                <div className="mt-2 w-48">
                    <div className="flex flex-row justify-between" id='exitReaderId'>
                        <Label
                            htmlFor="exitReaderId"
                            className="text-xs text-gray-600 mt-1" >{translate.byKey(TranslationKeys.exitReader)}:</Label>
                        {formikContext.errors.exitReaderId ? <span className='text-red-600 text-xs'>{formikContext.errors.exitReaderId}</span> : null}
                    </div>
                    <PaginatedSearchableSelectField
                        name={translate.byKey(TranslationKeys.exitReader)}
                        key="exitReaderId"
                        accessor='exitReaderId'
                        dataMethod={(listParameter: ListQuery) => getAvailableTerminals({ ...listParameter, filters: getReaderTypeFilters("exit",listParameter.filters)})}
                        searchAccessor={'devName'}
                        onDelete={(event) => deleteReader(formikContext.values.exitReaderId, 'exitReaderId')}
                        onSelect={(_,item) => {
                            formikContext.setFieldValue('exitReader', item?.label)
                            formikContext.setFieldValue('exitReaderId', item?.value)                            
                        }}                     
                        selectedItemLabel={formikContext.values.exitReader ?? ""}
                        dependent={{ accessor: 'zoneId', name: 'Zone', key: 'controller.zones.id', value: formikContext.values.zoneId ? formikContext.values.zoneId : 0, childAcceccor: 'exitReaderId' }}
                        disabled={!!currentItem.exitReaderId}/>
                </div>
            </div>
        </div></>;
};

export default DoorCard;
